import type { ReadonlyRefOrGetter } from "@vueuse/core";
import type { MaybeRefOrGetter, PropType, Ref } from "vue";

import { autoUpdate, flip, offset, useFloating } from "@floating-ui/vue";
import { onClickOutside, useFocusWithin } from "@vueuse/core";
import {
  computed,
  reactive,
  toRef,
  toValue,
  useId,
  watch,
  watchEffect,
} from "vue";

import { useI18nCommonFe } from "@solvari/translations";

import type { PrimitiveOrArrayValue } from "@/lib/components/logic/atoms/input/props";
import type { UseInputIconAtomEmit } from "@/lib/components/logic/atoms/input/useInputIconAtom";
import type { Size } from "@/lib/components/types";
import type {
  OptionValue,
  UseOptionsStoreScoped,
} from "@/lib/composables/useOptionsStore/useOptionsStore";
import type { UseValidationProviderEmits } from "@/lib/validation/ValidationProvider/useValidationProvider";

import { primitiveOrArrayValue } from "@/lib/components/logic/atoms/input/props";
import * as useInputIconAtom from "@/lib/components/logic/atoms/input/useInputIconAtom";
import {
  useElementSize,
  useIsOpen,
  useModel,
  useTemplateRef,
} from "@/lib/composables";
import {
  emitsDefinition,
  propsDefinition,
} from "@/lib/composables/componentComposable";
import { useActiveItem } from "@/lib/composables/useOptionsStore/useActiveItem";
import {
  useOptionsStore,
  useOptionsStoreScoped,
} from "@/lib/composables/useOptionsStore/useOptionsStore";
import { useValidationProviderEmits } from "@/lib/validation/ValidationProvider/useValidationProvider";

type UseSelectListboxAlignmentProps = {
  alignmentOffset?: number;
  alignmentOverflow?: number;
  alignmentPadding?: number;
};

type UseSelectProps = UseOptionsStoreScoped &
  UseSelectListboxAlignmentProps & {
    clearLabel?: string;
    disabled?: boolean;
    loading?: boolean;
    modelValue?: PrimitiveOrArrayValue;
    multiple?: boolean;
    name: string;
    openOnFocus?: boolean;
    placeholder?: string;
    size?: Size;
  };

type UseSelectEmit = UseInputIconAtomEmit &
  UseValidationProviderEmits & {
    (event: "blur" | "focus", value: string): void;
    (event: "update:modelValue", value: PrimitiveOrArrayValue): void;
  };

const useSelectListboxAlignmentProps = propsDefinition({
  alignmentOffset: { type: Number, default: 4 },
  alignmentPadding: { type: Number, default: 12 },
  alignmentOverflow: { type: Number, default: 0 },
});

const props = propsDefinition({
  ...useOptionsStoreScoped,
  name: { type: String, required: true },
  clearLabel: { type: String, required: false },
  placeholder: { type: String, required: false },
  multiple: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  modelValue: primitiveOrArrayValue,
  openOnFocus: { type: Boolean, default: false },
  size: { type: String as PropType<Size>, default: "md" },
  ...useSelectListboxAlignmentProps,
}) satisfies Record<keyof UseSelectProps, unknown>;

const emits = emitsDefinition([
  "update:modelValue",
  "focus",
  "blur",
  ...useInputIconAtom.emits,
  ...useValidationProviderEmits,
]);

function useSelectListboxAlignment(
  comboboxEl: Ref<HTMLElement | null>,
  listboxEl: Ref<HTMLElement | null>,
  alignmentOffset: MaybeRefOrGetter<number>,
  alignmentPadding: MaybeRefOrGetter<number>,
  alignmentOverflow: MaybeRefOrGetter<number>,
) {
  const { floatingStyles } = useFloating(comboboxEl, listboxEl, {
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: computed(() => {
      return [
        offset(toValue(alignmentOffset)),
        flip({ padding: toValue(alignmentPadding) }),
      ];
    }),
  });
  const { width } = useElementSize(comboboxEl);
  watchEffect(() => {
    if (!listboxEl.value) {
      return;
    }
    Object.assign(listboxEl.value.style, {
      ...floatingStyles.value,
      width: `${width.value + toValue(alignmentOverflow) * 2}px`,
      marginLeft: `-${toValue(alignmentOverflow)}px`,
    });
  });
}

function useSelectPlaceholder(
  placeholder: ReadonlyRefOrGetter<string | null | undefined>,
) {
  const { tr } = useI18nCommonFe();

  return computed(
    () =>
      toValue(placeholder) ?? tr("common_fe.fields.default.select.placeholder"),
  );
}

function use(props: UseSelectProps, emit: UseSelectEmit) {
  const modelValue = useModel("modelValue", props, emit, { local: true });

  const { isOpen, open, close, toggleOpen } = useIsOpen(
    { onOpen, onClose },
    { openable: () => !props.disabled },
  );

  // Translations
  const { tr } = useI18nCommonFe();
  const placeholder = useSelectPlaceholder(() => props.placeholder);
  const clearLabel = computed(
    () => props.clearLabel ?? tr("common_fe.fields.default.select.clear"),
  );

  // Ids
  const listboxId = useId();

  // Refs
  const { el: containerEl, ref: containerRef } = useTemplateRef();
  const { el: comboboxEl, ref: comboboxRef } = useTemplateRef();
  const { el: listboxEl, ref: listboxRef } = useTemplateRef();

  useSelectListboxAlignment(
    comboboxEl,
    listboxEl,
    () => props.alignmentOffset ?? 4,
    () => props.alignmentPadding ?? 12,
    () => props.alignmentOverflow ?? 0,
  );

  const { focused: isFocused } = useFocusWithin(containerEl);
  watch(isFocused, (isFocused) => {
    if (isFocused) {
      emit("focus", props.name);
      if (props.openOnFocus) {
        open();
      }
    } else {
      emit("blur", props.name);
    }
  });

  const {
    allItems,
    items,
    findItemIndex,
    tryToggleValue,
    setChecked,
    setIsOpen,
    activeItemValue,
    removeValue,
  } = useOptionsStore(modelValue, props);

  const multiValueShown = computed(() => {
    return !!allItems.value.length && props.multiple;
  });

  const {
    activeItem,
    activeDescendantId,
    setActiveItemByValue,
    setActiveItemByIndex,
    setNextActiveItem,
    setPreviousActiveItem,
    setFirstVisibleActive,
  } = useActiveItem(modelValue, activeItemValue, items, findItemIndex);

  function selectActiveItem() {
    if (activeItem.value) {
      tryToggleValue(activeItem.value.value);
    }
    itemSelected();
  }

  onClickOutside(containerEl, close);

  function onContainerClick(event: PointerEvent) {
    event.preventDefault();
    if (props.disabled) {
      return;
    }
    comboboxEl.value?.focus();
  }

  function onOpen() {
    setFirstVisibleActive();
  }

  function onClose() {
    activeItemValue.value = null;
  }

  function onListboxClick(value: OptionValue) {
    setActiveItemByValue(value);
  }

  function onListboxInput(
    checked: boolean,
    value: OptionValue | OptionValue[],
  ) {
    setChecked(checked, value);
    itemSelected();
  }

  function itemSelected() {
    if (!props.multiple) {
      close();
    }
  }

  function onComboboxKeydown(event: KeyboardEvent) {
    if (isOpen.value) {
      onKeydownOpen(event);
    } else {
      onKeydownClosed(event);
    }
  }

  function onKeydownClosed(event: KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case " ": {
        open();
        break;
      }
      case "Home": {
        open();
        setActiveItemByIndex(0);
        break;
      }
      case "End": {
        open();
        setActiveItemByIndex(items.value.length - 1);
        break;
      }
    }
  }

  function onKeydownOpen(event: KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown": {
        setNextActiveItem();
        break;
      }
      case "ArrowUp": {
        setPreviousActiveItem();
        break;
      }
      case "ArrowRight": {
        if (activeItem.value?.children?.length && !activeItem.value.isOpen) {
          setIsOpen(activeItem.value.value, true);
          setNextActiveItem();
        }
        break;
      }
      case "ArrowLeft": {
        if (activeItem.value?.children?.length && activeItem.value.isOpen) {
          setIsOpen(activeItem.value.value, false);
        }
        break;
      }
      case "Home": {
        setActiveItemByIndex(0);
        break;
      }
      case "End": {
        setActiveItemByIndex(items.value.length - 1);
        break;
      }
      case "PageDown": {
        setNextActiveItem(10);
        break;
      }
      case "PageUp": {
        setPreviousActiveItem(10);
        break;
      }
      case "Enter":
      case " ": {
        selectActiveItem();
        break;
      }
      case "Escape":
      case "Tab": {
        close();
        break;
      }
    }
  }

  /*
    Components
   */
  const container = { ref: containerRef, on: { mousedown: onContainerClick } };

  const comboboxAtom = {
    ref: comboboxRef,
    props: reactive({
      tabindex: toRef(() => (props.disabled ? -1 : 0)),
      "aria-expanded": toRef(() => (isOpen.value ? "true" : "false")),
      "aria-activedescendant": activeDescendantId,
      "aria-controls": listboxId,
      "aria-disabled": toRef(() => (props.disabled ? "true" : undefined)),
      role: "combobox",
      name: toRef(() => props.name),
      focused: isOpen,
    }),
    on: {
      click: toggleOpen,
      keydown: onComboboxKeydown,
    },
  };

  const comboboxText = computed(
    () => allItems.value[0]?.label ?? placeholder.value,
  );

  const listboxAtom = {
    ref: listboxRef,
    props: reactive({
      listboxId,
      items,
      size: toRef(() => props.size),
      multiple: toRef(() => props.multiple),
    }),
    on: {
      click: onListboxClick,
      "update:modelValue": onListboxInput,
      "update:isOpen": setIsOpen,
    },
  };

  const selectedCount = computed(() => allItems.value.length);

  function clearAll() {
    modelValue.value = [];
  }

  const multiValueDisplayAtom = reactive({
    if: multiValueShown,
    props: { items: allItems },
    on: { removeValue },
  });

  return {
    container,
    comboboxAtom,
    listboxAtom,
    isFocused,
    isOpen,
    multiValueDisplayAtom,
    comboboxText,
    placeholder,
    selectedCount,
    clearAll,
    clearLabel,
  };
}

export type { UseSelectEmit, UseSelectProps };

export {
  emits,
  props,
  use,
  useSelectListboxAlignment,
  useSelectListboxAlignmentProps,
  useSelectPlaceholder,
};
